"use client";
import React, { useState, useMemo, useEffect } from "react";
import CardGrid from "../../CardGrid";
import AnnouncementCard from "./components/AnnouncementCard";
import Button from "../../ui/Button";
import DefaultPageComponent from "../../ui/DefaultPageComponent";
import { DefaultSvg } from "../../Icon";
import { FilterBar } from "./components/HomeFilterBar";
import HeroPage from "./components/HeroPage";
import { Announcement, PaginationMeta } from "src/types";
import { throttle } from "lodash";
import { useAnnouncements } from "src/app/lib/useAnnouncementsPage";

const Search = ({
  setSearch,
}: {
  setSearch: (val: string | undefined) => void;
}) => {
  const [searchTerm, setSearchTerm] = useState("");

  // Memoized throttled function
  const throttledSearch = useMemo(
    () =>
      throttle((value: string) => {
        setSearch(value.trim() || undefined);
      }, 500), // 500ms throttle
    [setSearch]
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    throttledSearch(value);
  };

  const handleSearchSubmit = () => {
    throttledSearch.cancel(); // Cancel any pending throttled call
    setSearch(searchTerm.trim() || undefined);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      throttledSearch.cancel();
    };
  }, [throttledSearch]);

  return (
    <div className="flex h-full md:justify-start md:mt-5 items-end justify-end">
      <div className="bg-white rounded-lg xs:w-[90%] w-[60%] p-4 pr-0 py-0 flex justify-between">
        <div className="flex w-full gap-1 basis-full flex-1 items-center">
          <SearchIcon />
          <input
            placeholder="Search"
            value={searchTerm}
            onChange={handleSearchChange}
            className="outline-none block flex-1 w-full text-base placeholder:text-disabled-main focus:outline-none text-primary border-none"
            type="text"
          />
        </div>
        <div className="h-[50px] sm:h-[40px] cursor-pointer w-[60px]">
          <SearchBtn onClick={handleSearchSubmit} />
        </div>
      </div>
    </div>
  );
};

const Announcement1 = ({
  announcements,
}: {
  announcements: Announcement[];
}) => {
  // Lift search state here
  const [search, setSearch] = useState<string | undefined>(undefined);

  return (
    <section className="max-w-full overflow-clip">
      <HeroPage
        subHeader="Stay informed, inspired, and ahead with our comprehensive guides and tips tailored to your property journey."
        header="Announcements"
      >
        <Search setSearch={setSearch} />
      </HeroPage>
      <AnnouncementWrapper
        prefetchedAnnouncements={announcements}
        search={search}
      />
    </section>
  );
};

export default Announcement1;

export interface AnnouncementApiResponseMetadata {
  status: "success" | "error";
  total_announcement: number;
  total_announcement_month: number;
  total_announcement_view: number;
  total_announcement_month_view: number;
  data: Announcement[];
  pagination: PaginationMeta;
}

const AnnouncementWrapper = ({
  search,
  prefetchedAnnouncements,
}: {
  search?: string | undefined;
  prefetchedAnnouncements: Announcement[];
}) => {
  const {
    setSort,
    announcements,
    isLoading,
    showDefault,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useAnnouncements({ search, prefetchedAnnouncements });

  const isFiltering = !!search?.trim();

  return (
    <div className="wrapper py-6">
      <div className="sorts">
        <FilterBar setSort={setSort} setSearch={() => {}} />
      </div>

      {isLoading && !prefetchedAnnouncements?.length ? (
        <div>Loading announcements...</div>
      ) : isFiltering && announcements.length === 0 ? (
        <div>
          <DefaultPageComponent
            image="/images/not-found-optimize.gif"
            text={`No Results Found.
We couldn’t find any post matching your search. This might be due to limited availability, overly specific filters, or a typo in your search. Try using broader keywords, adjusting your filters, or simplifying your terms.`}
            svg={DefaultSvg}
          />
        </div>
      ) : showDefault ? (
        <DefaultPageComponent
          image="/images/not-found-optimize.gif"
          text={`We’re working on bringing you valuable insights, tips, and updates from the world of real estate, property management, market trends, and more. Stay tuned - great content is coming soon!`}
          svg={DefaultSvg}
        />
      ) : (
        <CardGrid minCardWidth={350}>
          {announcements.map((announcement) => (
            <AnnouncementCard
              className="!w-full"
              key={announcement.id}
              {...announcement}
            />
          ))}
        </CardGrid>
      )}

      {hasNextPage && (
        <Button
          className="w-max primary-btn mt-8 block mx-auto"
          onClick={() => fetchNextPage()}
          disabled={isFetchingNextPage}
        >
          {isFetchingNextPage ? "Loading..." : "Load more"}
        </Button>
      )}
    </div>
  );
};

export const SearchIcon = () => {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M10.4998 2C9.14436 2.00012 7.80863 2.32436 6.60402 2.94569C5.39941 3.56702 4.36086 4.46742 3.575 5.57175C2.78914 6.67609 2.27878 7.95235 2.08647 9.29404C1.89417 10.6357 2.02551 12.004 2.46954 13.2846C2.91357 14.5652 3.65741 15.7211 4.639 16.6557C5.62059 17.5904 6.81147 18.2768 8.11228 18.6576C9.41309 19.0384 10.7861 19.1026 12.1168 18.8449C13.4475 18.5872 14.6972 18.015 15.7618 17.176L19.4138 20.828C19.6024 21.0102 19.855 21.111 20.1172 21.1087C20.3794 21.1064 20.6302 21.0012 20.8156 20.8158C21.001 20.6304 21.1062 20.3796 21.1084 20.1174C21.1107 19.8552 21.0099 19.6026 20.8278 19.414L17.1758 15.762C18.1638 14.5086 18.7789 13.0024 18.9509 11.4157C19.1228 9.82905 18.8446 8.22602 18.148 6.79009C17.4514 5.35417 16.3646 4.14336 15.0121 3.29623C13.6595 2.44911 12.0957 1.99989 10.4998 2ZM3.99977 10.5C3.99977 8.77609 4.68458 7.12279 5.90357 5.90381C7.12256 4.68482 8.77586 4 10.4998 4C12.2237 4 13.877 4.68482 15.096 5.90381C16.3149 7.12279 16.9998 8.77609 16.9998 10.5C16.9998 12.2239 16.3149 13.8772 15.096 15.0962C13.877 16.3152 12.2237 17 10.4998 17C8.77586 17 7.12256 16.3152 5.90357 15.0962C4.68458 13.8772 3.99977 12.2239 3.99977 10.5Z"
        fill="#A4A7B0"
      />
    </svg>
  );
};

const SearchBtn = ({ onClick }: { onClick: () => void }) => {
  return (
    <svg
      onClick={onClick}
      width="100"
      height="100%"
      viewBox="0 0 58 54"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_2084_58294)">
        <path
          d="M0 0H50C54.4183 0 58 3.58172 58 8V46C58 50.4183 54.4183 54 50 54H0V0Z"
          fill="var(--primary-color)"
        />
        <path
          d="M20.4775 24.3044L21.1777 25.6898C21.6236 26.574 21.8457 27.0151 21.8457 27.5C21.8457 27.9849 21.6236 28.426 21.1777 29.3083L20.4775 30.6956C18.4847 34.6459 17.4883 36.6201 18.2642 37.6008C19.0417 38.5798 20.8831 37.6683 24.5628 35.8454L34.6652 30.8396C37.5545 29.4086 39 28.6922 39 27.5C39 26.3078 37.5545 25.5914 34.6652 24.1604L24.5628 19.1546C20.8831 17.3317 19.0417 16.4203 18.2642 17.3992C17.4867 18.3781 18.4847 20.3523 20.4775 24.3044Z"
          fill="white"
        />
      </g>
      <defs>
        <clipPath id="clip0_2084_58294">
          <path
            d="M0 0H50C54.4183 0 58 3.58172 58 8V46C58 50.4183 54.4183 54 50 54H0V0Z"
            fill="white"
          />
        </clipPath>
      </defs>
    </svg>
  );
};
